import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { usersApi } from "../api/users";
import { ApiError } from "../api/utils";
import { User } from "../modules/users/types";
import Header from "./Header";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/DashboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import EmployeesPage from "./pages/EmployeesPage";
import ScoringSetupPage from "./pages/ScoringSetupPage";
import ConnectionsPage from "./pages/ConnectionsPage";
import StyleGuidePage from "./pages/StyleGuidePage";
import SignUpPage from "./pages/SignUpPage";
import WaitlistPage from "./pages/WaitlistPage";
import FreeLandingPage from "./pages/FreeLandingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ErrorBoundary from "./ErrorBoundary";
import NotFoundPage from "./pages/NotFoundPage";
import AdminApp from "../admin/AdminApp";
import Spinner from "./ui/spinner";
import { Toaster } from "@/components/ui/sonner";

const App: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const user = await usersApi.getCurrentUser();
        if (isMounted) {
          setCurrentUser(user);
        }
      } catch (_err: unknown) {
        if (isMounted) {
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setIsCheckingAuth(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSignInSuccess = (user: User) => {
    setCurrentUser(user);
    navigate("/");
  };

  const handleSignUpSuccess = (user: User) => {
    setCurrentUser(user);
    navigate("/onboarding");
  };

  const handleSignOut = async () => {
    if (!window.confirm("Are you sure you want to sign out?")) {
      return;
    }

    setIsSigningOut(true);

    try {
      await usersApi.signOut();
      setCurrentUser(null);
      navigate("/");
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        console.error(err.message);
      } else {
        console.error("An error occurred during sign out");
      }
    } finally {
      setIsSigningOut(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Spinner size={32} className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/admin/*" element={<AdminApp />} />
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-background">
            <Toaster richColors position="bottom-right" />
            <Header user={currentUser} onSignOut={handleSignOut} isSigningOut={isSigningOut} />

            <ErrorBoundary>
              {currentUser ? (
                <main className="pb-8">
                  <Routes>
                    <Route path="/" element={<HomePage user={currentUser} />} />
                    <Route path="/onboarding" element={<OnboardingPage user={currentUser} />} />
                    <Route path="/employees" element={<EmployeesPage />} />
                    <Route path="/scoring-setup" element={<ScoringSetupPage />} />
                    <Route path="/connections" element={<ConnectionsPage />} />
                    <Route path="/waitlist" element={<WaitlistPage />} />
                    <Route path="/free" element={<FreeLandingPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsOfServicePage />} />
                    <Route path="/style-guide" element={<StyleGuidePage />} />
                    <Route path="/sign-in" element={<Navigate to="/" replace />} />
                    <Route path="/sign-up" element={<Navigate to="/" replace />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </main>
              ) : (
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route
                    path="/sign-in"
                    element={
                      <AuthPage
                        onSignInSuccess={handleSignInSuccess}
                      />
                    }
                  />
                  <Route
                    path="/sign-up"
                    element={
                      <SignUpPage
                        onSignUpSuccess={handleSignUpSuccess}
                      />
                    }
                  />
                  <Route path="/waitlist" element={<WaitlistPage />} />
                  <Route path="/free" element={<FreeLandingPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/style-guide" element={<StyleGuidePage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              )}
            </ErrorBoundary>
          </div>
        }
      />
    </Routes>
  );
};

export default App;
